import React, { useCallback, useId, useMemo, useRef, useState } from "react";
import { Activity, Loader2 } from "lucide-react";
import { getOutcomeDisplayPrice, formatChancePct } from "../../../utils/bondPredictHelpers";
import { getSelectedOutcomeLabel } from "../../../utils/bondPredictDetailHelpers";

const VIEW_W = 600;
const VIEW_H = 200;
const PAD_TOP = 10;
const PAD_BOTTOM = 18;

const RANGES = [
  { key: "1h", label: "1H", ms: 60 * 60 * 1000 },
  { key: "6h", label: "6H", ms: 6 * 60 * 60 * 1000 },
  { key: "1d", label: "1D", ms: 24 * 60 * 60 * 1000 },
  { key: "1w", label: "1W", ms: 7 * 24 * 60 * 60 * 1000 },
  { key: "1m", label: "1M", ms: 30 * 24 * 60 * 60 * 1000 },
  { key: "all", label: "All", ms: null },
];

const clamp01 = (v) => Math.min(1, Math.max(0, v));

const formatTick = (ts, spanMs) => {
  const d = new Date(ts);
  if (spanMs <= 24 * 60 * 60 * 1000) {
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  }
  if (spanMs <= 7 * 24 * 60 * 60 * 1000) {
    return d.toLocaleDateString([], { weekday: "short", hour: "2-digit" });
  }
  return d.toLocaleDateString([], { month: "short", day: "numeric" });
};

const formatHoverTime = (ts) =>
  new Date(ts).toLocaleString([], {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

/**
 * Price-over-time chart for the selected outcome, built from executed
 * trades (Kalshi/Polymarket style). Uses a step line since each trade
 * sets the price until the next one.
 */
const MarketDetailPriceChart = ({
  market,
  selectedOutcome,
  trades = [],
  status = "idle",
}) => {
  const gradId = useId();
  const surfaceRef = useRef(null);
  const [rangeKey, setRangeKey] = useState("all");
  const [hover, setHover] = useState(null);

  const outcomeLabel = getSelectedOutcomeLabel(selectedOutcome, market);
  const livePrice = getOutcomeDisplayPrice(selectedOutcome);

  // Full ascending series: optional initial price seed, then every trade.
  const fullSeries = useMemo(() => {
    const pts = [];
    const init = selectedOutcome?.metrics?.initialPrice;
    const initAt = selectedOutcome?.metrics?.initialPriceAt;
    if (typeof init === "number" && initAt) {
      pts.push({ t: new Date(initAt).getTime(), p: clamp01(init) });
    }
    for (let i = trades.length - 1; i >= 0; i -= 1) {
      const tr = trades[i];
      if (typeof tr?.price !== "number" || !tr.createdAt) continue;
      pts.push({ t: new Date(tr.createdAt).getTime(), p: clamp01(tr.price) });
    }
    pts.sort((a, b) => a.t - b.t);
    return pts;
  }, [trades, selectedOutcome]);

  const now = Date.now();
  const range = RANGES.find((r) => r.key === rangeKey) || RANGES[RANGES.length - 1];

  const { points, domainStart, domainEnd } = useMemo(() => {
    if (!fullSeries.length) return { points: [], domainStart: 0, domainEnd: 0 };
    const end = now;
    const start = range.ms ? end - range.ms : fullSeries[0].t;

    const visible = fullSeries.filter((pt) => pt.t >= start);
    // Carry the last price from before the window so the line starts at the edge.
    const before = fullSeries.filter((pt) => pt.t < start);
    if (before.length) {
      visible.unshift({ t: start, p: before[before.length - 1].p });
    }
    if (visible.length) {
      visible.push({ t: end, p: visible[visible.length - 1].p });
    }
    return { points: visible, domainStart: start, domainEnd: end };
  }, [fullSeries, range.ms, now]);

  const spanMs = Math.max(domainEnd - domainStart, 1);
  const innerH = VIEW_H - PAD_TOP - PAD_BOTTOM;

  const toX = useCallback(
    (t) => ((t - domainStart) / spanMs) * VIEW_W,
    [domainStart, spanMs]
  );
  const toY = useCallback((p) => PAD_TOP + (1 - p) * innerH, [innerH]);

  const { linePath, areaPath } = useMemo(() => {
    if (points.length < 2) return { linePath: "", areaPath: "" };
    let d = `M ${toX(points[0].t).toFixed(1)} ${toY(points[0].p).toFixed(1)}`;
    for (let i = 1; i < points.length; i += 1) {
      const x = toX(points[i].t).toFixed(1);
      const prevY = toY(points[i - 1].p).toFixed(1);
      const y = toY(points[i].p).toFixed(1);
      d += ` L ${x} ${prevY} L ${x} ${y}`;
    }
    const area = `${d} L ${VIEW_W} ${VIEW_H - PAD_BOTTOM} L 0 ${VIEW_H - PAD_BOTTOM} Z`;
    return { linePath: d, areaPath: area };
  }, [points, toX, toY]);

  const rangeChange = useMemo(() => {
    if (points.length < 2) return null;
    const first = points[0].p;
    const last = points[points.length - 1].p;
    return Math.round((last - first) * 100);
  }, [points]);

  const currentPct =
    livePrice != null
      ? Math.round(livePrice * 100)
      : points.length
      ? Math.round(points[points.length - 1].p * 100)
      : null;

  const handleMove = (event) => {
    if (points.length < 2 || !surfaceRef.current) return;
    const rect = surfaceRef.current.getBoundingClientRect();
    const frac = clamp01((event.clientX - rect.left) / rect.width);
    const t = domainStart + frac * spanMs;
    // Step chart: the active price is the latest point at or before t.
    let active = points[0];
    for (const pt of points) {
      if (pt.t <= t) active = pt;
      else break;
    }
    setHover({
      xFrac: clamp01((t - domainStart) / spanMs),
      yFrac: clamp01((toY(active.p) - 0) / VIEW_H),
      pct: Math.round(active.p * 100),
      t,
    });
  };

  const hasData = points.length >= 2;
  const loading = status === "loading" && !hasData;

  return (
    <section className="bp-detail-panel bp-detail-panel-chart">
      <div className="bp-detail-panel-head">
        <div>
          <p className="bp-detail-panel-kicker">Price history</p>
          <h2>
            {selectedOutcome ? outcomeLabel : "Select an outcome"}
            <span className="bp-price-chart-now tabular-nums">
              {" "}
              · {formatChancePct(
                livePrice != null
                  ? livePrice
                  : points.length
                  ? points[points.length - 1].p
                  : null
              )}
            </span>
          </h2>
        </div>
        <div className="bp-price-chart-head-trail">
          {rangeChange != null ? (
            <span
              className={`bp-price-chart-delta tabular-nums${
                rangeChange > 0
                  ? " bp-price-chart-delta-up"
                  : rangeChange < 0
                  ? " bp-price-chart-delta-down"
                  : ""
              }`}
            >
              {rangeChange > 0 ? "▲" : rangeChange < 0 ? "▼" : "—"}{" "}
              {Math.abs(rangeChange)} pts
            </span>
          ) : null}
          <div className="bp-price-chart-ranges" role="tablist" aria-label="Chart range">
            {RANGES.map((r) => (
              <button
                key={r.key}
                type="button"
                role="tab"
                aria-selected={rangeKey === r.key}
                className={`bp-price-chart-range${
                  rangeKey === r.key ? " bp-price-chart-range-on" : ""
                }`}
                onClick={() => setRangeKey(r.key)}
              >
                {r.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="bp-price-chart-empty">
          <Loader2 size={18} className="bp-spin" />
        </div>
      ) : !hasData ? (
        <div className="bp-price-chart-empty">
          <Activity size={16} />
          <span>No trades yet — the chart draws as this outcome trades.</span>
        </div>
      ) : (
        <div
          ref={surfaceRef}
          className="bp-price-chart-surface"
          onPointerMove={handleMove}
          onPointerLeave={() => setHover(null)}
        >
          <svg
            viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
            preserveAspectRatio="none"
            className="bp-price-chart-svg"
            role="img"
            aria-label={`${outcomeLabel} price history`}
          >
            <defs>
              <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#64b5f6" stopOpacity="0.32" />
                <stop offset="100%" stopColor="#1e88e5" stopOpacity="0.01" />
              </linearGradient>
            </defs>
            {[0, 0.25, 0.5, 0.75, 1].map((ratio) => (
              <line
                key={ratio}
                x1={0}
                y1={PAD_TOP + innerH * ratio}
                x2={VIEW_W}
                y2={PAD_TOP + innerH * ratio}
                className={`bp-price-chart-grid${
                  ratio === 0.5 ? " bp-price-chart-grid-mid" : ""
                }`}
                vectorEffect="non-scaling-stroke"
              />
            ))}
            <path d={areaPath} fill={`url(#${gradId})`} />
            <path
              d={linePath}
              className="bp-price-chart-line"
              fill="none"
              vectorEffect="non-scaling-stroke"
            />
            {hover ? (
              <line
                x1={hover.xFrac * VIEW_W}
                y1={PAD_TOP}
                x2={hover.xFrac * VIEW_W}
                y2={VIEW_H - PAD_BOTTOM}
                className="bp-price-chart-crosshair"
                vectorEffect="non-scaling-stroke"
              />
            ) : null}
          </svg>

          <div className="bp-price-chart-yaxis" aria-hidden>
            <span>100%</span>
            <span>50%</span>
            <span>0%</span>
          </div>

          <div className="bp-price-chart-xaxis" aria-hidden>
            <span>{formatTick(domainStart, spanMs)}</span>
            <span>{formatTick(domainStart + spanMs / 2, spanMs)}</span>
            <span>Now</span>
          </div>

          {hover ? (
            <>
              <span
                className="bp-price-chart-hover-dot"
                style={{
                  left: `${hover.xFrac * 100}%`,
                  top: `${hover.yFrac * 100}%`,
                }}
                aria-hidden
              />
              <div
                className={`bp-price-chart-tooltip tabular-nums${
                  hover.xFrac > 0.72 ? " bp-price-chart-tooltip-left" : ""
                }`}
                style={{ left: `${hover.xFrac * 100}%` }}
              >
                <strong>{formatChancePct(hover.pct / 100)}</strong>
                <span>{formatHoverTime(hover.t)}</span>
              </div>
            </>
          ) : null}
        </div>
      )}
    </section>
  );
};

export default MarketDetailPriceChart;
